import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, GripVertical, Save, Menu, X } from 'lucide-react';
import type { TabConfig, QuestionConfig } from '../../types/api';
import { wizardService } from '../../services/wizardService';
import LoadingSpinner from '../shared/LoadingSpinner';

interface QuestionsAdminProps {
  onPreview?: (config: TabConfig[]) => void;
}

interface EditingTab {
  id?: string;
  title: string;
  weightPct: number;
  index: number;
}

interface EditingQuestion {
  id?: string;
  text: string;
  order: number;
}

export default function QuestionsAdmin({
  onPreview,
}: QuestionsAdminProps): ReactElement {
  const [tabs, setTabs] = useState<TabConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados de edición
  const [editingTab, setEditingTab] = useState<EditingTab | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<{
    tabId: string;
    question: EditingQuestion;
  } | null>(null);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);

  // Estado para mobile drawer
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Cargar configuración inicial
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const tabs = await wizardService.config.getAllTabs();
        setTabs(tabs);
        if (tabs.length > 0 && !selectedTabId) {
          setSelectedTabId(tabs[0]?.id ?? null);
        }
      } catch (err) {
        setError('Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    void loadData();
  }, [selectedTabId]);

  const loadConfig = async (): Promise<void> => {
    try {
      setLoading(true);
      const tabs = await wizardService.config.getAllTabs();
      setTabs(tabs);
      if (tabs.length > 0 && !selectedTabId) {
        setSelectedTabId(tabs[0]?.id ?? null);
      }
    } catch (err) {
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  // ===== TAB MANAGEMENT =====
  const handleCreateTab = (): void => {
    const newIndex = Math.max(0, ...tabs.map((t) => t.index)) + 1;
    setEditingTab({
      title: '',
      weightPct: 10,
      index: newIndex,
    });
  };

  const handleEditTab = (tab: TabConfig): void => {
    setEditingTab({
      id: tab.id,
      title: tab.title,
      weightPct: tab.weightPct,
      index: tab.index,
    });
  };

  const handleSaveTab = async (): Promise<void> => {
    if (!editingTab) return;

    try {
      setSaving(true);

      if (editingTab.id) {
        // Actualizar
        await wizardService.config.updateTab(editingTab.id, {
          title: editingTab.title,
          weightPct: editingTab.weightPct,
          index: editingTab.index,
        });
      } else {
        // Crear
        const newTab = await wizardService.config.createTab({
          title: editingTab.title,
          weightPct: editingTab.weightPct,
          index: editingTab.index,
        });
        setSelectedTabId(newTab.id);
      }

      setEditingTab(null);
      await loadConfig();
    } catch (err) {
      setError('Error al guardar el tab');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTab = async (tabId: string): Promise<void> => {
    if (
      !confirm(
        '¿Estás seguro de eliminar este tab? Se perderán todas las preguntas asociadas.'
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      await wizardService.config.deleteTab(tabId);
      if (selectedTabId === tabId) {
        setSelectedTabId(tabs.find((t) => t.id !== tabId)?.id ?? null);
      }
      await loadConfig();
    } catch (err) {
      setError('Error al eliminar el tab');
    } finally {
      setSaving(false);
    }
  };

  // ===== QUESTION MANAGEMENT =====
  const selectedTab = tabs.find((t) => t.id === selectedTabId);

  const handleCreateQuestion = (): void => {
    if (!selectedTabId) return;

    const maxOrder = selectedTab?.questions.length
      ? Math.max(...selectedTab.questions.map((q) => q.order))
      : 0;

    setEditingQuestion({
      tabId: selectedTabId,
      question: {
        text: '',
        order: maxOrder + 1,
      },
    });
  };

  const handleEditQuestion = (question: QuestionConfig): void => {
    if (!selectedTabId) return;

    setEditingQuestion({
      tabId: selectedTabId,
      question: {
        id: question.id,
        text: question.text || '',
        order: question.order || 1,
      },
    });
  };

  const handleSaveQuestion = async (): Promise<void> => {
    if (!editingQuestion) return;

    try {
      setSaving(true);

      if (editingQuestion.question.id) {
        // Actualizar
        await wizardService.config.updateQuestion(editingQuestion.question.id, {
          text: editingQuestion.question.text,
          order: editingQuestion.question.order,
        });
      } else {
        // Crear
        await wizardService.config.createQuestion(editingQuestion.tabId, {
          text: editingQuestion.question.text,
          order: editingQuestion.question.order,
        });
      }

      setEditingQuestion(null);
      await loadConfig();
    } catch (err) {
      setError('Error al guardar la pregunta');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string): Promise<void> => {
    if (!confirm('¿Estás seguro de eliminar esta pregunta?')) {
      return;
    }

    try {
      setSaving(true);
      await wizardService.config.deleteQuestion(questionId);
      await loadConfig();
    } catch (err) {
      setError('Error al eliminar la pregunta');
    } finally {
      setSaving(false);
    }
  };

  const totalWeight = tabs.reduce((sum, tab) => sum + tab.weightPct, 0);

  if (loading) {
    return (
      <LoadingSpinner className="h-96" message="Cargando configuración..." />
    );
  }

  // Componente del sidebar (reutilizable para desktop y mobile)
  const TabsSidebar = ({ onClose }: { onClose?: () => void }) => (
    <div className="h-full bg-white flex flex-col">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="font-medium text-gray-900">Tabs de Evaluación</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                selectedTabId === tab.id
                  ? 'border-primary-medium bg-primary-light/10'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => {
                setSelectedTabId(tab.id);
                onClose?.();
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-sm">#{tab.index}</span>
                  </div>
                  <h3 className="font-medium text-gray-900 mt-1">
                    {tab.title}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {tab.weightPct}% • {tab.questions.length} preguntas
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditTab(tab);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDeleteTab(tab.id);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Botón hamburguesa para mobile */}
            <button
              onClick={() => {
                setMobileDrawerOpen(true);
              }}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Gestión de Preguntas
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Peso total:{' '}
                <span
                  className={
                    totalWeight === 100 ? 'text-green-600' : 'text-red-600'
                  }
                >
                  {totalWeight}%
                </span>
                {totalWeight !== 100 && ' (debe sumar 100%)'}
              </p>
            </div>
          </div>
          <button
            onClick={handleCreateTab}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-primary-dark text-white rounded-md hover:bg-primary-medium flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4" />
            <span className="sm:inline">Nuevo Tab</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 px-4 py-3 mx-4 sm:mx-6 mt-4 rounded-md">
          <p className="text-red-700 text-sm">{error}</p>
          <button
            onClick={() => {
              setError(null);
            }}
            className="text-red-600 hover:text-red-800 underline text-xs mt-1"
          >
            Cerrar
          </button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar desktop */}
        <div className="hidden lg:block w-80 bg-white border-r border-gray-200 overflow-y-auto">
          <TabsSidebar />
        </div>

        {/* Mobile drawer */}
        {mobileDrawerOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={() => {
                setMobileDrawerOpen(false);
              }}
            />
            <div className="fixed inset-y-0 left-0 w-80 z-50 lg:hidden">
              <TabsSidebar
                onClose={() => {
                  setMobileDrawerOpen(false);
                }}
              />
            </div>
          </>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {selectedTab ? (
            <div className="p-4 sm:p-6">
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-base sm:text-lg font-medium text-gray-900">
                        {selectedTab.title}
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Peso: {selectedTab.weightPct}% •{' '}
                        {selectedTab.questions.length} preguntas
                      </p>
                    </div>
                    <button
                      onClick={handleCreateQuestion}
                      className="w-full sm:w-auto px-3 py-2.5 sm:py-2 bg-primary-dark text-white rounded-md hover:bg-primary-medium flex items-center justify-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Nueva Pregunta
                    </button>
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  {selectedTab.questions.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-sm sm:text-base">
                        No hay preguntas en este tab
                      </p>
                      <button
                        onClick={handleCreateQuestion}
                        className="mt-2 text-primary-dark hover:text-primary-medium underline text-sm"
                      >
                        Crear primera pregunta
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedTab.questions
                        .sort((a, b) => a.order - b.order)
                        .map((question) => (
                          <div
                            key={question.id}
                            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <span className="text-xs text-gray-500 font-medium">
                                    #{question.order}
                                  </span>
                                </div>
                                <p className="text-gray-900 text-sm sm:text-base break-words">
                                  {question.text}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {question.points || 5} puntos
                                </p>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
                                <button
                                  onClick={() => {
                                    handleEditQuestion(question);
                                  }}
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    void handleDeleteQuestion(question.id)
                                  }
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-4">
              <div className="text-center">
                <p className="text-gray-500 text-sm sm:text-base">
                  Selecciona un tab para ver sus preguntas
                </p>
                <button
                  onClick={() => {
                    setMobileDrawerOpen(true);
                  }}
                  className="mt-3 lg:hidden text-primary-dark hover:text-primary-medium underline text-sm"
                >
                  Ver tabs disponibles
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de edición de Tab - Responsive */}
      {editingTab && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-medium mb-4">
                {editingTab.id ? 'Editar Tab' : 'Nuevo Tab'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título
                  </label>
                  <input
                    type="text"
                    value={editingTab.title}
                    onChange={(e) => {
                      setEditingTab({ ...editingTab, title: e.target.value });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 focus:outline-none focus:ring-2 focus:ring-primary-medium text-base sm:text-sm"
                    placeholder="Ej: Planeación"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peso (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={editingTab.weightPct || 0}
                    onChange={(e) => {
                      setEditingTab({
                        ...editingTab,
                        weightPct: parseFloat(e.target.value) || 0,
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 focus:outline-none focus:ring-2 focus:ring-primary-medium text-base sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orden
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingTab.index || 1}
                    onChange={(e) => {
                      setEditingTab({
                        ...editingTab,
                        index: parseInt(e.target.value) || 1,
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 focus:outline-none focus:ring-2 focus:ring-primary-medium text-base sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-6">
                <button
                  onClick={() => {
                    setEditingTab(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => void handleSaveTab()}
                  disabled={saving || !(editingTab.title || '').trim()}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-primary-dark text-white rounded-md hover:bg-primary-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de Pregunta - Responsive */}
      {editingQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-medium mb-4">
                {editingQuestion.question.id
                  ? 'Editar Pregunta'
                  : 'Nueva Pregunta'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texto de la pregunta
                  </label>
                  <textarea
                    value={editingQuestion.question.text || ''}
                    onChange={(e) => {
                      setEditingQuestion({
                        ...editingQuestion,
                        question: {
                          ...editingQuestion.question,
                          text: e.target.value,
                        },
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 focus:outline-none focus:ring-2 focus:ring-primary-medium text-base sm:text-sm"
                    rows={4}
                    placeholder="Ej: ¿Se realizó un saludo cordial?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orden
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingQuestion.question.order || 1}
                    onChange={(e) => {
                      setEditingQuestion({
                        ...editingQuestion,
                        question: {
                          ...editingQuestion.question,
                          order: parseInt(e.target.value) || 1,
                        },
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 focus:outline-none focus:ring-2 focus:ring-primary-medium text-base sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-6">
                <button
                  onClick={() => {
                    setEditingQuestion(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => void handleSaveQuestion()}
                  disabled={
                    saving || !(editingQuestion.question.text || '').trim()
                  }
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-primary-dark text-white rounded-md hover:bg-primary-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
